import type { DandyMouthArchItemsProps } from './DandyMouthArchItems';
import type { TeethLayoutProps, SingleMouthItem, BridgeGroupProps } from './DandyTeethLayout';
import { TeethLayout } from './DandyTeethLayout';
import type { ToothNumber } from '@orthly/items';
import type { Theme } from '@orthly/ui-primitives';
import { FlossPalette, createStyles, makeStyles, Fade, Grid, Slide } from '@orthly/ui-primitives';
import React from 'react';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            // overflowX: 'auto',
            // overflowY: 'hidden',
            height: '100%',
            '& .MuiGrid-container': {
                // height: '100%',
                position: 'relative',
            },
            '& path': {
                transition: theme.transitions.create(['fill', 'stroke'], {
                    duration: 250,
                    easing: theme.transitions.easing.easeInOut,
                }),
            },
        },
        labelGrid: {
            height: 'auto',
        },
        verticalSpacer: {
            height: 10,
        },
        horizontalToothGrid: {
            flexDirection: 'column',
            width: '50%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
        },
        verticalToothGrid: {
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            flexWrap: 'nowrap',
            alignItems: 'center',
        },
    }),
);

export interface DandyMouthDisplayProps {
    fullMouth?: DandyMouthArchItemsProps;
    items: SingleMouthItem[];
    bridges: BridgeGroupProps[];
    verticalLayout: boolean;
    animate: boolean;
    onToothClick?: (tooth: ToothNumber, event: React.MouseEvent<SVGGElement, MouseEvent>) => void;
    archLabels?: boolean;
}

const commonSlideProps = {
    appear: true,
    enter: true,
    exit: true,
    unmountOnExit: true,
    mountOnEnter: true,
    timeout: { exit: 350, enter: 350, appear: 350 },
};
const commonFadeProps = { ...commonSlideProps, timeout: { exit: 250, enter: 300, appear: 300 } };

const HorizontalMouthInner: React.FC<TeethLayoutProps> = props => {
    const classes = useStyles();
    // Slide transition requires that this be statically defined otherwise throws a runtime error
    const HorizontalContent = (
        <Grid container className={classes.horizontalToothGrid}>
            <TeethLayout {...props} />
        </Grid>
    );
    return props.animate ? (
        <Slide
            direction={props.type === 'upper' ? 'right' : 'left'}
            in={!props.verticalLayout}
            {...commonSlideProps}
            enter={false}
        >
            {HorizontalContent}
        </Slide>
    ) : (
        HorizontalContent
    );
};

const HorizontalMouthLayout: React.FC<DandyMouthDisplayProps> = props => {
    if (props.verticalLayout) {
        return props.archLabels ? (
            <svg width={'250'} height={'32'} viewBox={'0 0 250 32'} fill={'none'} xmlns={'http://www.w3.org/2000/svg'}>
                <path
                    d={
                        'M 8.7389 24.66 V 14.5364 H 12.1596 C 12.9505 14.5364 13.5997 14.6715 14.1072 14.9417 C 14.6147 15.2086 14.9903 15.5761 15.2343 16.044 C 15.4781 16.512 15.6 17.0442 15.6 17.6407 C 15.6 18.2372 15.4781 18.7661 15.2343 19.2275 C 14.9903 19.6888 14.6164 20.0513 14.1121 20.315 C 13.608 20.5753 12.9637 20.7055 12.1793 20.7055 H 9.4112 V 19.5982 H 12.1399 C 12.6803 19.5982 13.1153 19.5191 13.4449 19.3609 C 13.7777 19.2027 14.0183 18.9786 14.1665 18.6886 C 14.3181 18.3953 14.3939 18.046 14.3939 17.6407 C 14.3939 17.2353 14.3181 16.8811 14.1665 16.5779 C 14.0149 16.2747 13.7727 16.0407 13.4399 15.876 C 13.1071 15.7079 12.6671 15.6239 12.12 15.6239 H 9.9649 V 24.66 H 8.7389 Z M 13.5042 20.1123 L 15.9955 24.66 H 14.5718 L 12.12 20.1123 H 13.5042 Z M 17.3455 24.66 V 17.0673 H 18.512 V 24.66 H 17.3455 Z M 17.9386 15.8018 C 17.7112 15.8018 17.5152 15.7244 17.3504 15.5695 C 17.1889 15.4146 17.1082 15.2284 17.1082 15.0109 C 17.1082 14.7934 17.1889 14.6072 17.3504 14.4523 C 17.5152 14.2975 17.7112 14.22 17.9386 14.22 C 18.166 14.22 18.3605 14.2975 18.522 14.4523 C 18.6867 14.6072 18.769 14.7934 18.769 15.0109 C 18.769 15.2284 18.6867 15.4146 18.522 15.5695 C 18.3605 15.7244 18.166 15.8018 17.9386 15.8018 Z M 23.5743 27.6654 C 23.0108 27.6654 22.5264 27.5929 22.121 27.4479 C 21.7157 27.3063 21.3779 27.1185 21.1076 26.8845 C 20.8407 26.6538 20.6282 26.4066 20.47 26.1429 L 21.3993 25.4904 C 21.5048 25.6289 21.6382 25.787 21.7997 25.965 C 21.9611 26.1463 22.182 26.3028 22.4621 26.4346 C 22.7455 26.5697 23.1163 26.6373 23.5743 26.6373 C 24.1873 26.6373 24.693 26.4889 25.0919 26.1924 C 25.4905 25.8958 25.69 25.4311 25.69 24.7985 V 23.2561 H 25.5911 C 25.5054 23.3945 25.3835 23.566 25.2254 23.7702 C 25.0704 23.9713 24.8464 24.1508 24.553 24.3091 C 24.263 24.4639 23.8708 24.5414 23.3766 24.5414 C 22.7636 24.5414 22.2133 24.3964 21.7255 24.1064 C 21.2411 23.8164 20.8572 23.3945 20.5738 22.841 C 20.2936 22.2873 20.1536 21.615 20.1536 20.8241 C 20.1536 20.0464 20.2904 19.3692 20.5639 18.7925 C 20.8374 18.2125 21.218 17.7643 21.7058 17.4479 C 22.1935 17.1282 22.757 16.9684 23.3964 16.9684 C 23.8907 16.9684 24.2829 17.0508 24.5729 17.2156 C 24.8661 17.3771 25.0902 17.5616 25.2451 17.7692 C 25.4033 17.9735 25.5252 18.1416 25.6108 18.2734 H 25.7295 V 17.0673 H 26.8566 V 24.8775 C 26.8566 25.53 26.7083 26.0605 26.4117 26.4692 C 26.1183 26.8811 25.7229 27.1827 25.2254 27.3738 C 24.731 27.5682 24.1807 27.6654 23.5743 27.6654 Z M 23.5348 23.4935 C 24.0027 23.4935 24.3982 23.3863 24.7211 23.1721 C 25.0441 22.9579 25.2896 22.6498 25.4576 22.2477 C 25.6257 21.8457 25.7098 21.3646 25.7098 20.8043 C 25.7098 20.2573 25.6273 19.7745 25.4626 19.356 C 25.2979 18.9375 25.0539 18.6096 24.731 18.3723 C 24.408 18.135 24.0093 18.0164 23.5348 18.0164 C 23.0404 18.0164 22.6285 18.1416 22.2989 18.3921 C 21.9727 18.6425 21.7272 18.9786 21.5624 19.4005 C 21.401 19.8223 21.3202 20.2902 21.3202 20.8043 C 21.3202 21.3316 21.4026 21.7979 21.5673 22.2032 C 21.7354 22.6053 21.9826 22.9217 22.3089 23.1523 C 22.6383 23.3797 23.047 23.4935 23.5348 23.4935 Z M 30.0194 20.0925 V 24.66 H 28.8528 V 14.5364 H 30.0194 V 18.2536 H 30.1183 C 30.2962 17.8615 30.5631 17.5501 30.9191 17.3194 C 31.2783 17.0854 31.7562 16.9684 32.3526 16.9684 C 32.87 16.9684 33.3231 17.0722 33.7119 17.2798 C 34.1009 17.4842 34.4024 17.7989 34.6166 18.224 C 34.8341 18.6458 34.9428 19.183 34.9428 19.8355 V 24.66 H 33.7762 V 19.9146 C 33.7762 19.3115 33.6197 18.8452 33.3066 18.5156 C 32.9969 18.1828 32.5668 18.0164 32.0165 18.0164 C 31.6341 18.0164 31.2915 18.0971 30.9883 18.2586 C 30.6884 18.4201 30.4512 18.6557 30.2765 18.9655 C 30.1051 19.2752 30.0194 19.6509 30.0194 20.0925 Z M 40.2398 17.0673 V 18.0559 H 36.3051 V 17.0673 H 40.2398 Z M 37.4519 15.2482 H 38.6185 V 22.485 C 38.6185 22.8145 38.6663 23.0617 38.7619 23.2264 C 38.8607 23.3879 38.986 23.4967 39.1376 23.5527 C 39.2925 23.6054 39.4556 23.6319 39.6269 23.6319 C 39.7554 23.6319 39.8609 23.6252 39.9433 23.612 C 40.0257 23.5955 40.0916 23.5824 40.141 23.5725 L 40.3783 24.6204 C 40.2992 24.6501 40.1888 24.6798 40.0472 24.7095 C 39.9054 24.7424 39.7258 24.7589 39.5083 24.7589 C 39.1788 24.7589 38.8558 24.688 38.5394 24.5463 C 38.2264 24.4046 37.966 24.1888 37.7584 23.8988 C 37.5541 23.6088 37.4519 23.2429 37.4519 22.8014 V 15.2482 Z'
                    }
                    fill={FlossPalette.LIGHT_GRAY}
                />
                <path
                    d={
                        'M 210.2125 24.66 V 14.5364 H 211.4386 V 23.5725 H 216.1436 V 24.66 H 210.2125 Z M 220.9286 24.8182 C 220.1966 24.8182 219.5656 24.6567 219.0354 24.3338 C 218.5076 24.0075 218.1005 23.5527 217.814 22.9695 C 217.5309 22.3829 217.3894 21.7007 217.3894 20.923 C 217.3894 20.1452 217.5309 19.4598 217.814 18.8666 C 218.1005 18.2701 218.4995 17.8055 219.0099 17.4726 C 219.525 17.1365 220.1247 16.9684 220.8102 16.9684 C 221.2046 16.9684 221.5956 17.0343 221.9807 17.1661 C 222.367 17.298 222.7173 17.5122 223.034 17.8088 C 223.3506 18.1021 223.6024 18.4909 223.7903 18.9753 C 223.9782 19.4598 224.0722 20.0563 224.0722 20.7648 V 21.2591 H 218.22 V 20.2507 H 222.8855 C 222.8855 19.8223 222.7996 19.44 222.6291 19.1039 C 222.4609 18.7677 222.2196 18.5025 221.9076 18.308 C 221.5967 18.1136 221.2313 18.0164 220.8102 18.0164 C 220.3451 18.0164 219.9426 18.1317 219.6038 18.3624 C 219.2674 18.5898 219.0088 18.8864 218.8278 19.2522 C 218.6457 19.618 218.5552 20.0101 218.5552 20.4286 V 21.1009 C 218.5552 21.6744 218.655 22.1604 218.8522 22.5592 C 219.0528 22.9546 219.3312 23.2561 219.6874 23.4638 C 220.0435 23.668 220.4576 23.7702 220.9286 23.7702 C 221.2348 23.7702 221.512 23.7274 221.7591 23.6417 C 222.0097 23.5527 222.2254 23.421 222.4064 23.2463 C 222.5874 23.0683 222.7277 22.8475 222.8263 22.5839 L 223.9538 22.9002 C 223.8344 23.2825 223.636 23.6186 223.3553 23.9086 C 223.0757 24.1954 222.7289 24.4195 222.3171 24.581 C 221.9053 24.7391 221.4424 24.8182 220.9286 24.8182 Z M 229.0892 17.0673 V 18.0559 H 224.9955 V 17.0673 H 229.0892 Z M 226.2216 24.66 V 16.0193 C 226.2216 15.5843 226.3237 15.2218 226.5279 14.9318 C 226.732 14.6418 226.9977 14.4243 227.3236 14.2793 C 227.6508 14.1343 227.9941 14.0618 228.3572 14.0618 C 228.6437 14.0618 228.878 14.0849 229.059 14.131 C 229.24 14.1772 229.3757 14.22 229.4638 14.2596 L 229.1286 15.268 C 229.0694 15.2482 228.9871 15.2235 228.8815 15.1938 C 228.7794 15.1642 228.6437 15.1493 228.4755 15.1493 C 228.0904 15.1493 227.812 15.2465 227.6403 15.441 C 227.4721 15.6354 227.3886 15.9205 227.3886 16.2961 V 24.66 H 226.2216 Z M 233.8869 17.0673 V 18.0559 H 229.9522 V 17.0673 H 233.8869 Z M 231.0983 15.2482 H 232.2652 V 22.485 C 232.2652 22.8145 232.3128 23.0617 232.4091 23.2264 C 232.5077 23.3879 232.633 23.4967 232.7838 23.5527 C 232.9392 23.6054 233.1028 23.6319 233.2733 23.6319 C 233.402 23.6319 233.5076 23.6252 233.59 23.612 C 233.6723 23.5955 233.7384 23.5824 233.7872 23.5725 L 234.025 24.6204 C 233.9461 24.6501 233.8359 24.6798 233.6932 24.7095 C 233.5517 24.7424 233.3719 24.7589 233.155 24.7589 C 232.8255 24.7589 232.5019 24.688 232.1864 24.5463 C 231.8732 24.4046 231.6122 24.1888 231.4045 23.8988 C 231.2004 23.6088 231.0983 23.2429 231.0983 22.8014 V 15.2482 Z'
                    }
                    fill={FlossPalette.LIGHT_GRAY}
                />
            </svg>
        ) : null;
    }
    return props.animate ? (
        <Fade in={!props.verticalLayout} {...commonFadeProps}>
            <Grid
                container
                justifyContent={'center'}
                style={{ position: props.verticalLayout ? 'absolute' : undefined }}
            >
                <HorizontalMouthInner type={'upper'} {...props} />
                <HorizontalMouthInner type={'lower'} {...props} />
            </Grid>
        </Fade>
    ) : (
        <Grid container justifyContent={'center'} style={{ position: props.verticalLayout ? 'absolute' : undefined }}>
            <HorizontalMouthInner type={'upper'} {...props} />
            <HorizontalMouthInner type={'lower'} {...props} />
        </Grid>
    );
};

const verticalViewSlideProps = { ...commonSlideProps, timeout: { ...commonSlideProps.timeout, exit: 600 } };
const verticalViewFadeProps = { ...commonFadeProps, timeout: { ...commonFadeProps.timeout, exit: 200 } };

const VerticalMouthLayout: React.FC<TeethLayoutProps> = props => {
    const classes = useStyles();

    if (!props.verticalLayout) {
        return null;
    }

    if (
        (!!props.fullMouth?.upperArchHidden && props.type === 'upper') ||
        (!!props.fullMouth?.lowerArchHidden && props.type === 'lower')
    ) {
        return null;
    }

    const VerticalMouthInner = (
        <Grid container direction={'column'} wrap={'nowrap'}>
            <Grid container className={classes.verticalToothGrid}>
                <TeethLayout {...props} />
            </Grid>
        </Grid>
    );
    return props.animate ? (
        <Fade in={props.verticalLayout} {...verticalViewFadeProps}>
            <Slide
                direction={props.type === 'upper' ? 'down' : 'up'}
                in={props.verticalLayout}
                {...verticalViewSlideProps}
            >
                {VerticalMouthInner}
            </Slide>
        </Fade>
    ) : (
        VerticalMouthInner
    );
};

export const DandyMouthDisplay: React.FC<DandyMouthDisplayProps> = props => {
    const classes = useStyles();
    return (
        <Grid container justifyContent={'center'} className={classes.root}>
            <VerticalMouthLayout {...props} type={'upper'} />
            <HorizontalMouthLayout {...props} />
            <VerticalMouthLayout {...props} type={'lower'} />
        </Grid>
    );
};
